import type {
  PersonBasicResponse,
  PersonMemberResponse,
  PersonV1CurrentResponse,
} from 'client/types.gen';

const API_BASE_URL = import.meta.env.VITE_APP_BASE_URL || '';

export type ProfileImageFields = {
  image?: string | null;
  person_image?: string | null;
  profile_img?: string | null;
  profile_image_url?: string | null;
};

export function isAbsoluteImageUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

/**
 * Builds a URL for GET /api/v1/files/image. The endpoint is public, so this is
 * safe to use in <img src> without auth headers (unlike the generated client).
 */
export function getProfileImageUrl(storageKey: string, size: number): string {
  if (isAbsoluteImageUrl(storageKey)) {
    return storageKey;
  }

  const params = new URLSearchParams({
    name: storageKey,
    size: String(size),
  });

  return `${API_BASE_URL}/api/v1/files/image?${params.toString()}`;
}

export function resolveProfileImageSrc({
  profileImageUrl,
  size,
  storageKey,
}: {
  profileImageUrl?: string | null;
  size: number;
  storageKey?: string | null;
}): string | undefined {
  if (profileImageUrl) {
    return profileImageUrl;
  }

  if (!storageKey) {
    return undefined;
  }

  return getProfileImageUrl(storageKey, size);
}

export function getAvatarImageProps(entity?: ProfileImageFields | null): {
  image: string | null | undefined;
  profileImageUrl?: string | null;
} {
  return {
    image: entity?.image ?? entity?.profile_img ?? entity?.person_image ?? null,
    profileImageUrl: entity?.profile_image_url ?? undefined,
  };
}

/** @deprecated Use getAvatarImageProps */
export function profileImageFields(entity?: ProfileImageFields | null): {
  image?: string | null;
  profile_image_url: string | null;
} {
  const props = getAvatarImageProps(entity);
  return {
    image: props.image,
    profile_image_url: props.profileImageUrl ?? null,
  };
}

type ActivityActor = Pick<
  PersonV1CurrentResponse,
  | 'email'
  | 'first_name'
  | 'id'
  | 'last_name'
  | 'name'
  | 'person_image'
  | 'profile_image_url'
  | 'uuid'
>;

type ActivityMember = Pick<
  PersonMemberResponse,
  'id' | 'image' | 'name' | 'profile_image_url'
>;

/** Build a PersonBasicResponse-shaped user for optimistic activity rows. */
export function optimisticActivityUser(
  actor: ActivityActor,
  options?: {
    member?: ActivityMember;
    memberId?: string | null;
    members?: ActivityMember[];
    name?: string;
    uuid?: string | null;
  },
): Pick<PersonBasicResponse, 'image' | 'profile_image_url' | 'name' | 'uuid'> {
  const memberId = options?.memberId ?? options?.member?.id;
  const resolvedMemberId = memberId ?? undefined;
  const member =
    options?.member ??
    (resolvedMemberId && options?.members
      ? options.members.find((item) => item.id === resolvedMemberId)
      : undefined);

  if (member) {
    const avatar = getAvatarImageProps(member);
    return {
      image: avatar.image ?? null,
      profile_image_url: avatar.profileImageUrl ?? null,
      name: options?.name ?? member.name,
      uuid: options?.uuid ?? member.id,
    };
  }

  const avatar = getAvatarImageProps(actor);
  const name =
    options?.name ??
    (`${actor.first_name ?? ''} ${actor.last_name ?? ''}`.trim() ||
      actor.name ||
      actor.email ||
      '');

  return {
    image: avatar.image ?? null,
    profile_image_url: avatar.profileImageUrl ?? null,
    name,
    uuid: options?.uuid ?? actor.uuid ?? actor.id!,
  };
}
